// Cross-tab session guard.
//
// The bug this fixes: Tab A logs in as Doctor X, Tab B then logs in as Doctor Y
// in the same browser. localStorage is shared, so Tab B overwrites the token
// and the user ID, but Tab A still holds Doctor X's data in Redux state.
// When Tab A makes an API call it now sends Doctor Y's token with Doctor X's
// userId rendered in the UI — backend returns Doctor Y's data, mismatching what
// the page is displaying.
//
// How this guards against it:
//   - On login we generate a sessionId, store it in BOTH sessionStorage
//     (per-tab, never shared) and localStorage (shared across tabs).
//   - The shared id is owned by whichever tab logged in most recently. Other
//     tabs detect a mismatch (their per-tab id != shared id) and exile
//     themselves to /login. They never write back to the shared id, so the
//     legitimate owner is not knocked out.
//   - An "exile" flag in sessionStorage persists across reloads of the stale
//     tab so it stays on /login even after refresh, until the user signs in
//     again in that tab.
//
// The check is wired into PrivateRoute, the axios request interceptor, the
// `storage` event, and tab focus / visibility — so a stale tab is caught
// either before it renders, before it calls an API, or as soon as the user
// switches back to it.

const TAB_SESSION_KEY = 'tabSessionId';
const ACTIVE_SESSION_KEY = 'activeSessionId';
const TAB_EXILED_KEY = 'tabExiled';

const generateSessionId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

// Called from the login success path. Binds a fresh id to this tab and
// publishes it as the active session for the browser. Also clears any prior
// exile flag so a previously-exiled tab can transition back into a logged-in
// state via a fresh login.
export const bindSession = () => {
  const sid = generateSessionId();
  try {
    sessionStorage.setItem(TAB_SESSION_KEY, sid);
    sessionStorage.removeItem(TAB_EXILED_KEY);
    localStorage.setItem(ACTIVE_SESSION_KEY, sid);
  } catch { /* storage disabled — best effort */ }
  return sid;
};

// True if this tab is stale: either it was previously exiled, or its per-tab
// id no longer matches the active one (another tab logged in/out as a
// different user). Returns false for tabs that never logged in (no per-tab id
// and no exile flag) so public pages are unaffected.
export const isSessionMismatch = () => {
  try {
    if (sessionStorage.getItem(TAB_EXILED_KEY) === '1') return true;
    const tabSid = sessionStorage.getItem(TAB_SESSION_KEY);
    if (!tabSid) return false;
    const activeSid = localStorage.getItem(ACTIVE_SESSION_KEY);
    return tabSid !== activeSid;
  } catch {
    return false;
  }
};

// Exile this tab to /login. CRITICAL: do NOT touch localStorage — another tab
// may legitimately own the active session, and clearing the shared session id
// here would knock that tab out too (storage event would fire there with
// activeSessionId=null and that tab would think it had been kicked out).
//
// User-initiated logout (authSlice logout reducer) is what clears localStorage;
// that path is reserved for the tab that owns the session.
//
// Idempotent: safe to call repeatedly. We skip the navigation if we're
// already on /login so a focus / storage event chain can't loop.
export const forceLogout = () => {
  try {
    sessionStorage.setItem(TAB_EXILED_KEY, '1');
    sessionStorage.removeItem(TAB_SESSION_KEY);
  } catch { /* noop */ }

  const hash = (typeof window !== 'undefined' && window.location.hash) || '';
  if (hash.toLowerCase().startsWith('#/login')) return;

  try { window.dispatchEvent(new Event('logout')); } catch { /* noop */ }
  try { window.location.hash = '#/login'; } catch {
    try { window.location.href = '/'; } catch { /* noop */ }
  }
};
